import React from 'react'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { spacing } from '@mobi/component-library/Theme/Common'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { store } from '@core/Store'
import { getFeatures } from '@core/State/FeatureFlags/Store/selectors'
import { openShareOutgoing } from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/emitter'
import { getKambiPendingBetCoupon } from '@core/Areas/KambiSports/helpers/api/player'

export const ShareBetButton: React.FC<{ couponRef: string }> = ({ couponRef }) => {
  const features = getFeatures(store.getState())
  const isBetLoopSportsFeatureActive = features['bet-loop-sports']
  if (!isBetLoopSportsFeatureActive) return null

  const cleanCouponRefValue = Number.parseInt(couponRef)
  if (!cleanCouponRefValue || typeof cleanCouponRefValue !== 'number') return null

  return <ShareBetButtonComponent couponRef={cleanCouponRefValue} />
}

// ===============
// Local Component
// ===============

const ShareBetButtonComponent: React.FC<{ couponRef: number }> = ({ couponRef }) => {
  const shareButtonRef = React.useRef<HTMLButtonElement>(null)
  const rootContainerRef = React.useRef<HTMLDivElement>(null)

  const [refCurr, setRefCurr] = React.useState<HTMLDivElement | null>(null)
  const [shareCoupon, setShareCoupon] = React.useState<KambiHistoryCoupon | null>(null)
  const [isFetchingCouponData, setIsFetchingCouponData] = React.useState(false)

  // Force re-render to apply styles
  React.useEffect(() => {
    setRefCurr(rootContainerRef.current)
  }, [])

  const handleShareClick = () => {
    if (shareCoupon) {
      openShareOutgoing({ contextType: 'kambi', share: { coupon: shareCoupon } })
      return
    }

    setIsFetchingCouponData(true)
    getKambiPendingBetCoupon(couponRef)
      .then(response => {
        if (!response) throw Error('Kambi: No coupon found')
        const coupon = response[0]
        openShareOutgoing({ contextType: 'kambi', share: { coupon } })
        setShareCoupon(coupon)
      })
      .catch(() => {
        const title = "Sorry, we're unable to share at this time"
        addToast({ id: 'kambi-share-error', title, icon: 'warning', theme: 'dark' })
      })
      .finally(() => setIsFetchingCouponData(false))
  }

  return (
    <div ref={rootContainerRef} style={{ minHeight: '4rem' }}>
      <CacheProvider
        value={createCache({
          prepend: true,
          key: 'kambi-bet-slip-share-button',
          container: refCurr as HTMLDivElement,
        })}
      >
        {refCurr && (
          <div style={{ padding: `${spacing.smx1} 1rem 0` }}>
            <Button
              isFullWidth
              ref={shareButtonRef}
              color='secondary_grey'
              onClick={handleShareClick}
              data-testid='ShareBetButton'
              isLoading={isFetchingCouponData}
            >
              <Icon name='LineShare02' size='2rem' /> Share Bet
            </Button>
          </div>
        )}
      </CacheProvider>
    </div>
  )
}
